import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Route, X, MapPin, Navigation, ChevronDown, RotateCcw } from 'lucide-react';
import useRouteCalculator from '@/hooks/useRouteCalculator';
import { selectPOIs } from '@/store/selectors';
import { formatDistance, formatDuration } from '@/lib/utils';

const GLASS_STYLE = {
  background: 'rgba(255,255,255,0.88)',
  backdropFilter: 'blur(40px)',
  WebkitBackdropFilter: 'blur(40px)',
};

const DARK_GLASS_STYLE = {
  background: 'rgba(15,23,42,0.88)',
  backdropFilter: 'blur(40px)',
  WebkitBackdropFilter: 'blur(40px)',
};

export default function RouteControl({ mapRef }) {
  const { t } = useTranslation();
  const devices = useSelector((state) => state.devices.items);
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  const positions = useSelector((state) => state.session.positions);
  const poiList = useSelector(selectPOIs);
  const staffByUserId = useSelector((state) => state.staff?.byUserId ?? {});
  const selectedStaffUserId = useSelector((state) => state.staff?.selectedUserId);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [freeformMode, setFreeformMode] = useState(false);
  const [showPOIList, setShowPOIList] = useState(false);

  const {
    from, to, routes, selectedRouteIndex, selectedRoute,
    isCalculating, isSelectingPoint, error, needsRecalculate,
    routeGeoJSON,
    setFrom, setTo, startSelectingPoint, cancelSelectingPoint,
    calculateRoute, calculateRouteDebounced, selectRoute, clearRoute,
  } = useRouteCalculator();

  const deviceList = Object.values(devices)
    .filter((d) => !d.hidden_from_default_list)
    .sort((a, b) => a.name.localeCompare(b.name));
  // Only staff users with a usable last fix can be a routing start point.
  const staffList = Object.values(staffByUserId)
    .filter((s) => s.lastFix?.latitude != null && s.lastFix?.longitude != null)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Auto-fill "from" with the currently-selected asset when opening. Vehicle
  // selection wins if both happen to be set (mutual-exclusion is enforced
  // upstream by selectStaff/selectVehicle, so this is mostly belt-and-braces).
  useEffect(() => {
    if (!open || from || freeformMode) return;
    if (selectedDeviceId) {
      const device = devices[selectedDeviceId];
      const pos = positions[selectedDeviceId];
      if (device && pos && pos.latitude !== 0) {
        setFrom({
          lng: pos.longitude,
          lat: pos.latitude,
          label: device.name,
          type: 'vehicle',
          deviceId: selectedDeviceId,
        });
        return;
      }
    }
    if (selectedStaffUserId) {
      const staff = staffByUserId[selectedStaffUserId];
      if (staff?.lastFix) {
        setFrom({
          lng: staff.lastFix.longitude,
          lat: staff.lastFix.latitude,
          label: staff.name || `User ${String(selectedStaffUserId).slice(0, 8)}`,
          type: 'staff',
          userId: selectedStaffUserId,
        });
      }
    }
  }, [open, from, freeformMode, selectedDeviceId, devices, positions, selectedStaffUserId, staffByUserId, setFrom]);

  // Push route data to map when routes change
  useEffect(() => {
    if (routeGeoJSON && active) {
      mapRef.current?.setRouteData(routeGeoJSON);
      mapRef.current?.fitRouteBounds(routeGeoJSON);
    }
  }, [routeGeoJSON, active, mapRef]);

  // Add/update draggable pin markers when from/to change and routes exist
  useEffect(() => {
    if (!active || routes.length === 0) return;
    mapRef.current?.setRouteMarkers({
      from,
      to,
      onDragEnd: (which, lngLat) => {
        const point = { lng: lngLat.lng, lat: lngLat.lat, label: `${lngLat.lat.toFixed(5)}, ${lngLat.lng.toFixed(5)}`, type: 'pin' };
        if (which === 'from') {
          setFrom(point);
        } else {
          setTo(point);
        }
        calculateRouteDebounced(
          which === 'from' ? point : from,
          which === 'to' ? point : to
        );
      },
    });
  }, [active, routes.length, from, to, setFrom, setTo, calculateRouteDebounced, mapRef]);

  // Update start marker position when vehicle moves (live tracking)
  useEffect(() => {
    if (!active || !from || from.type !== 'vehicle') return;
    mapRef.current?.updateStartMarker(from);
  }, [active, from, mapRef]);

  // Listen for alt route clicks from MapView
  useEffect(() => {
    const handler = (e) => {
      selectRoute(e.detail.index);
      if (routeGeoJSON) {
        const updated = {
          ...routeGeoJSON,
          features: routeGeoJSON.features.map((f) => ({
            ...f,
            properties: { ...f.properties, selected: f.properties.index === e.detail.index },
          })),
        };
        mapRef.current?.setRouteData(updated);
      }
    };
    document.addEventListener('route-alt-click', handler);
    return () => document.removeEventListener('route-alt-click', handler);
  }, [selectRoute, routeGeoJSON, mapRef]);

  // Map click handler for point selection
  useEffect(() => {
    if (!isSelectingPoint) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    map.getCanvas().style.cursor = 'crosshair';

    const handleClick = (e) => {
      const point = {
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
        label: `${e.lngLat.lat.toFixed(5)}, ${e.lngLat.lng.toFixed(5)}`,
        type: 'pin',
      };

      if (isSelectingPoint === 'from') {
        setFrom(point);
        cancelSelectingPoint();
        if (freeformMode && !to) {
          setTimeout(() => startSelectingPoint('to'), 0);
          return;
        }
      } else {
        setTo(point);
        cancelSelectingPoint();
      }
    };

    map.once('click', handleClick);

    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        cancelSelectingPoint();
      }
    };
    document.addEventListener('keydown', handleKeydown);

    return () => {
      map.off('click', handleClick);
      document.removeEventListener('keydown', handleKeydown);
      map.getCanvas().style.cursor = '';
    };
  }, [isSelectingPoint, freeformMode, to, setFrom, setTo, startSelectingPoint, cancelSelectingPoint, mapRef]);

  const handleToggle = () => {
    if (active) {
      mapRef.current?.clearRoute();
      clearRoute();
      setActive(false);
      setOpen(false);
      setFreeformMode(false);
      setShowPOIList(false);
    } else {
      setOpen(!open);
    }
  };

  // Select value carries kind via a "v:<deviceId>" or "s:<userId>" prefix so
  // a single dropdown can route to either source without an extra field.
  const handleFromChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    if (val.startsWith('v:')) {
      const id = Number(val.slice(2));
      const device = devices[id];
      const pos = positions[id];
      if (device && pos) {
        setFrom({
          lng: pos.longitude,
          lat: pos.latitude,
          label: device.name,
          type: 'vehicle',
          deviceId: id,
        });
      }
    } else if (val.startsWith('s:')) {
      const userId = val.slice(2);
      const staff = staffByUserId[userId];
      if (staff?.lastFix) {
        setFrom({
          lng: staff.lastFix.longitude,
          lat: staff.lastFix.latitude,
          label: staff.name || `User ${String(userId).slice(0, 8)}`,
          type: 'staff',
          userId,
        });
      }
    }
  };

  const handlePOISelect = (poi) => {
    // Supabase POIs have latitude/longitude columns directly (no WKT)
    if (poi.latitude != null && poi.longitude != null) {
      setTo({
        lng: poi.longitude,
        lat: poi.latitude,
        label: poi.name,
        type: 'poi',
      });
    }
    setShowPOIList(false);
  };

  const handleCalculate = async () => {
    const success = await calculateRoute();
    if (success) {
      setActive(true);
      setOpen(false);
    }
    // On failure, error is shown in the dropdown (which stays open)
  };

  const handleRecalculate = async () => {
    await calculateRoute();
  };

  const formatETA = (durationSeconds) => {
    const eta = new Date(Date.now() + durationSeconds * 1000);
    return eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const canCalculate = from && to && !isCalculating;

  return (
    <div className="relative">
      {/* Toggle button */}
      <button
        onClick={handleToggle}
        className={`h-9 px-3.5 text-xs font-semibold rounded-xl flex items-center gap-2 transition-all shadow-md ${
          active
            ? 'bg-blue-500 text-white shadow-blue-500/25'
            : 'bg-white/90 text-slate-600 hover:bg-white border border-slate-200/80 dark:bg-slate-800/90 dark:text-slate-300 dark:border-slate-700/80 dark:hover:bg-slate-800'
        }`}
        style={{ backdropFilter: 'blur(12px)' }}
        title={t('route.title', 'Route Calculator')}
      >
        <Route className="w-3.5 h-3.5" />
        {t('route.short', 'Route')}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute top-11 end-0 z-50 w-[280px] rounded-2xl shadow-xl border border-white/30 dark:border-slate-700/50 p-4 space-y-3"
          style={GLASS_STYLE}
        >
          <div className="hidden dark:block absolute inset-0 rounded-2xl pointer-events-none" style={DARK_GLASS_STYLE} />

          <div className="relative z-10 space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                {t('route.title', 'Route Calculator')}
              </span>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* From field */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  {t('route.from', 'From')}
                </label>
                <button
                  onClick={() => {
                    setFreeformMode(!freeformMode);
                    if (!freeformMode) {
                      setFrom(null);
                    } else if (selectedDeviceId) {
                      const device = devices[selectedDeviceId];
                      const pos = positions[selectedDeviceId];
                      if (device && pos) {
                        setFrom({ lng: pos.longitude, lat: pos.latitude, label: device.name, type: 'vehicle', deviceId: selectedDeviceId });
                      }
                    }
                  }}
                  className="text-[10px] text-blue-500 hover:text-blue-600 font-medium"
                >
                  {freeformMode ? t('route.useAsset', 'Use asset') : t('route.dropPin', 'Drop pin')}
                </button>
              </div>

              {freeformMode ? (
                <button
                  onClick={() => startSelectingPoint('from')}
                  className={`w-full h-8 px-2.5 text-xs rounded-lg border text-start flex items-center gap-2 transition-all ${
                    isSelectingPoint === 'from'
                      ? 'border-blue-500 bg-blue-50/60 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'border-slate-200/80 bg-white/60 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200'
                  }`}
                >
                  <MapPin className="w-3 h-3 text-emerald-500 shrink-0" />
                  {from ? <bdi>{from.label}</bdi> : (
                    <span className="text-slate-400">{isSelectingPoint === 'from' ? t('route.clickMap', 'Click the map...') : t('route.setStart', 'Click to set start')}</span>
                  )}
                </button>
              ) : (
                <select
                  value={from?.type === 'vehicle'
                    ? `v:${from.deviceId}`
                    : from?.type === 'staff'
                      ? `s:${from.userId}`
                      : ''}
                  onChange={handleFromChange}
                  className="w-full h-8 px-2.5 text-xs rounded-lg border border-slate-200/80 bg-white/60 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                >
                  <option value="">{t('route.selectAsset', 'Select asset...')}</option>
                  {deviceList.length > 0 && (
                    <optgroup label={t('heatmap.vehicles', 'Vehicles')}>
                      {deviceList.map((d) => (
                        <option key={`v:${d.id}`} value={`v:${d.id}`}>{d.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {staffList.length > 0 && (
                    <optgroup label={t('heatmap.staffSection', 'People')}>
                      {staffList.map((s) => (
                        <option key={`s:${s.userId}`} value={`s:${s.userId}`}>
                          {s.name || `User ${String(s.userId).slice(0, 8)}`}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
            </div>

            {/* To field */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 block">
                {t('route.to', 'To')}
              </label>
              <button
                onClick={() => {
                  if (showPOIList) {
                    setShowPOIList(false);
                  } else {
                    startSelectingPoint('to');
                  }
                }}
                className={`w-full h-8 px-2.5 text-xs rounded-lg border text-start flex items-center gap-2 transition-all ${
                  isSelectingPoint === 'to'
                    ? 'border-blue-500 bg-blue-50/60 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'border-slate-200/80 bg-white/60 text-slate-700 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-200'
                }`}
              >
                <MapPin className="w-3 h-3 text-red-500 shrink-0" />
                {to ? <bdi>{to.label}</bdi> : (
                  <span className="text-slate-400">{isSelectingPoint === 'to' ? t('route.clickMap', 'Click the map...') : t('route.setDest', 'Click to set destination')}</span>
                )}
              </button>

              {/* POI quick-pick */}
              {poiList.length > 0 && (
                <button
                  onClick={() => setShowPOIList(!showPOIList)}
                  className="mt-1.5 text-[10px] text-blue-500 hover:text-blue-600 font-medium flex items-center gap-1"
                >
                  <Navigation className="w-2.5 h-2.5" />
                  {t('route.pickPOI', 'Pick from POIs')}
                  <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showPOIList ? 'rotate-180' : ''}`} />
                </button>
              )}

              {showPOIList && (
                <div className="mt-1.5 max-h-28 overflow-y-auto rounded-lg border border-slate-200/80 bg-white/60 dark:bg-slate-800/60 dark:border-slate-600">
                  {poiList.map((poi) => (
                    <button
                      key={poi.id}
                      onClick={() => handlePOISelect(poi)}
                      className="w-full text-start px-2.5 py-1.5 text-[11px] text-slate-700 dark:text-slate-200 hover:bg-slate-100/60 dark:hover:bg-slate-700/60 transition-colors truncate"
                    >
                      {poi.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <p className="text-[11px] text-red-500 dark:text-red-400">{error}</p>
            )}

            {/* Calculate button */}
            <button
              onClick={handleCalculate}
              disabled={!canCalculate}
              className="w-full h-9 text-xs font-semibold rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center justify-center gap-2"
            >
              {isCalculating ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('route.calculating', 'Calculating...')}
                </>
              ) : (
                <>
                  <Route className="w-3.5 h-3.5" />
                  {t('route.calculate', 'Calculate Route')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Results card (shown when active with routes) */}
      {active && selectedRoute && !open && (
        <div
          className="absolute top-11 end-0 z-50 w-[280px] rounded-2xl shadow-xl border border-white/30 dark:border-slate-700/50 p-4 space-y-3"
          style={GLASS_STYLE}
        >
          <div className="hidden dark:block absolute inset-0 rounded-2xl pointer-events-none" style={DARK_GLASS_STYLE} />

          <div className="relative z-10 space-y-3">
            {/* Results header */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                {t('route.results', 'Route Results')}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setOpen(true)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-0.5"
                  title={t('route.edit', 'Edit')}
                >
                  <Route className="w-3 h-3" />
                </button>
                <button
                  onClick={handleToggle}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-0.5"
                  title={t('route.close', 'Close')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Main result */}
            <div className="text-center py-1">
              <div className="text-lg font-bold text-slate-800 dark:text-slate-100">
                {formatDistance(selectedRoute.distance)} km
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                {formatDuration(selectedRoute.duration)} · {t('route.arrives', 'Arrives')} ~{formatETA(selectedRoute.duration)}
              </div>
            </div>

            {/* From → To summary */}
            <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
              <MapPin className="w-3 h-3 text-emerald-500 shrink-0" />
              <span className="truncate"><bdi>{from?.label}</bdi></span>
              <span className="text-slate-300 dark:text-slate-600">→</span>
              <MapPin className="w-3 h-3 text-red-500 shrink-0" />
              <span className="truncate"><bdi>{to?.label}</bdi></span>
            </div>

            {/* Recalculate badge */}
            {needsRecalculate && (
              <button
                onClick={handleRecalculate}
                className="w-full h-7 text-[11px] font-medium rounded-lg bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700 flex items-center justify-center gap-1.5 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                {from?.type === 'staff'
                  ? t('route.recalculateStaff', 'Person moved — Recalculate')
                  : t('route.recalculate', 'Vehicle moved — Recalculate')}
              </button>
            )}

            {/* Alternative routes */}
            {routes.length > 1 && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 block">
                  {t('route.alternatives', 'Alternative Routes')}
                </label>
                <div className="space-y-1">
                  {routes.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        selectRoute(i);
                        mapRef.current?.highlightRoute(i);
                      }}
                      className={`w-full text-start px-2.5 py-1.5 text-[11px] rounded-lg transition-all flex items-center justify-between ${
                        i === selectedRouteIndex
                          ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700'
                          : 'text-slate-600 hover:bg-slate-100/60 dark:text-slate-300 dark:hover:bg-slate-700/60'
                      }`}
                    >
                      <span>{t('route.routeN', 'Route {{n}}', { n: i + 1 })}</span>
                      <span className="text-slate-400 dark:text-slate-500">
                        {formatDistance(r.distance)} km · {formatDuration(r.duration)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Clear button */}
            <button
              onClick={handleToggle}
              className="w-full h-8 text-[11px] font-medium rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-100/60 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700/60 transition-all flex items-center justify-center gap-1.5"
            >
              <X className="w-3 h-3" />
              {t('route.clear', 'Clear Route')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
